/* eslint-disable react-hooks/exhaustive-deps */
// React Imports
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Icon Imports
import ICONS from "../../../../assets/Icons";

// Redux Imports

import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import {
  reimbursementRequestApproval,
  reimbursementRequestViewAll,
} from "../../../../redux/features/reimbursementRequest";
import {
  reimbursementTxnViewAll,
  resetToggle,
} from "../../../../redux/features/reimbursementTransaction";

// Component Imports
import TertiaryTopRoundButton from "../../../../components/_utils/CommonButtons/TertiaryTopRoundButton/TertiaryTopRoundButton";
import TopBarButtons from "../../../../components/_utils/CommonButtons/TopBarButton";
import CommonCardDashboard from "../../../../components/_utils/CommonCardDashboard";
import WeeklySchedulesCard from "../../../../components/_utils/WeeklySchedulesCard";

// Utils Imports
import {
  GlobalConfiguration,
  PaginationConfiguration,
  TimelineConfiguration,
} from "../../../../utils/configurationProvider";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import TEXT from "../../../../utils/globalText";

const ReimbursementTransactionsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentSlide, setCurrentSlide] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );
  const [txnCountLoading, setTxnCountLoading] = useState(true);

  const services = useSelector((state) => state.refreshToken.services);
  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);

  const {
    reimbursementRequestViewAllData,
    reimbursementRequestViewAllLoading,
  } = useSelector((state) => state.reimbursementRequest);

  const { reimbursementRequestApprovalData } = useSelector(
    (state) => state.reimbursementRequest,
  );

  const {
    reimbursementTxnViewAllLoading,
    summary: txnSummary,
    totalCount: txnTotalCount,
  } = useSelector((state) => state.reimbursementTransaction);

  const pendingRequestCount =
    reimbursementRequestApprovalData?.total_count ||
    GlobalConfiguration?.lengthCheckZero;

  const requireApprovalTxnCount =
    txnSummary?.actionable_transaction_count ||
    GlobalConfiguration?.lengthCheckZero;

  const pendingTxnCount =
    txnSummary?.pending_transaction_count ||
    GlobalConfiguration?.lengthCheckZero;

  const approvedTxnCount =
    txnSummary?.approved_transaction_count ||
    GlobalConfiguration?.lengthCheckZero;

  const isViewAllDisabled =
    reimbursementTxnViewAllLoading ||
    txnCountLoading ||
    txnTotalCount === GlobalConfiguration?.lengthCheckZero;

  const hasCReimbursementTxn = services.includes("CReimbursementTxn");
  const hasAReimbursementRequest = services.includes("AReimbursementRequest");

  const onMount = () => {
    (async () => {
      await Promise.allSettled([
        dispatch(
          reimbursementTxnViewAll({
            page: GlobalConfiguration?.lengthCheckOne,
            page_size: PaginationConfiguration.paginationSmall,
          }),
        ),
        dispatch(
          reimbursementRequestViewAll({
            page: GlobalConfiguration?.lengthCheckOne,
            page_size: PaginationConfiguration.paginationSmall,
          }),
        ),
      ]);
      setTxnCountLoading(false);
    })();
  };

  const handleDatacardClick = (approvalStatus) => {
    const routeMap = {
      approved:
        "/reimbursement_transaction_dashboard/reimbursement_transaction_view_all",
      pending:
        "/reimbursement_transaction_dashboard/reimbursement_transaction_view_all",
      require:
        "/reimbursement_transaction_dashboard/reimbursement_transaction_single_approval",
    };

    navigate(routeMap[approvalStatus], {
      state: {
        approvalStatus,
      },
    });
  };

  const formatPendingCount = (count, services, serviceName) => {
    const hasService = (services || []).includes(serviceName);

    if (!hasService) {
      return "N/A";
    }

    if (!count || count === GlobalConfiguration?.lengthCheckZero) {
      return "00";
    }

    const digitCount = count.toString().length;

    if (digitCount <= GlobalConfiguration?.categoryminLength) {
      return count
        .toLocaleString("en-IN")
        .padStart(TimelineConfiguration?.barWidthMedium, "0");
    }

    const maxForDigits =
      Math.pow(
        GlobalConfiguration?.panLength,
        digitCount - GlobalConfiguration?.lengthCheckOne,
      ) - GlobalConfiguration?.lengthCheckOne;

    return `${maxForDigits.toLocaleString("en-IN")}+`;
  };

  const slides = Array.from(
    { length: GlobalConfiguration?.lengthCheckZero },
    (_, i) => `Slide ${i + GlobalConfiguration?.lengthCheckOne}`,
  );

  const nextSlide = () => {
    if (currentSlide < slides.length - GlobalConfiguration?.lengthCheckOne) {
      setCurrentSlide(currentSlide + GlobalConfiguration?.lengthCheckOne);
    }
  };

  const prevSlide = () => {
    if (currentSlide > GlobalConfiguration?.lengthCheckZero) {
      setCurrentSlide(currentSlide - GlobalConfiguration?.lengthCheckOne);
    }
  };

  const CREATE_REQUEST_SERVICES = ["CReimbursementTxn"];

  const handleCreateRequest = () => {
    const userServices = services || [];

    const hasAnyService = CREATE_REQUEST_SERVICES.some((s) =>
      userServices.includes(s),
    );

    if (hasAnyService) {
      navigate(
        "/reimbursement_transaction_dashboard/create_reimbursement_request",
      );
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: CREATE_REQUEST_SERVICES },
      });
    }
  };

  const handleViewRequest = () => {
    if ((services || []).includes("VReimbursementTxn")) {
      navigate(
        "/reimbursement_transaction_dashboard/reimbursement_request_view_all",
      );
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: "VReimbursementTxn" },
      });
    }
  };

  const requests = reimbursementRequestViewAllData?.requests || [];

  const safeServices = Array.isArray(services) ? services : [];

  const hasTxnApprovalService = services?.includes("AReimbursementTxn");

  const approvalService = "AReimbursementRequest";
  const hasApprovalRequestService = safeServices.includes(approvalService);
  const hasPendingRequests =
    pendingRequestCount > GlobalConfiguration?.lengthCheckZero;

  const handleRequestNavigation = () => {
    if (!hasApprovalRequestService) {
      navigate("/feature_not_assigned", {
        state: { unavailableService: approvalService },
      });
      return;
    }
    if (!hasPendingRequests || reimbursementRequestViewAllLoading) {
      return;
    }

    navigate(
      "/reimbursement_transaction_dashboard/reimbursement_request_single_approval",
    );
  };

  useEffect(() => {
    dispatch(
      reimbursementRequestApproval({
        entityId: markerData?.entity_id,
      }),
    );
  }, [markerData]);

  useEffect(() => {
    dispatch(enableSwitcher());
    onMount();
    return () => {
      dispatch(disableSwitcher());
    };
  }, [markerData]);

  return (
    <div
      style={{ height: "var(--full-height)" }}
      className={` internalDashboardView bg-primary-bg w-width-full px-padding-md overflow-y-auto Qlg:px-padding-base pb-padding-md`}
    >
      <div className="h-navbar-height Qsmd:h-navbar-breadcrumb-height"></div>
      <div className="pb-padding-lg">
        <div className="flex flex-col mt-margin-md gap-gap-md Qlg:mx-auto pb-padding-xl Qlg:pb-padding-base max-w-tab-width-normal w-width-full Qlg:flex-row mb-margin-md Qlg:mb-margin-base Qlg:relative">
          <div className="Qlg:w-dashboard-left-card-width flex flex-col">
            <div className="w-width-full p-padding-sm fixed top-top-base left-left-base right-right-base bg-text-primary flex items-center z-z-index-tooltip Qlg:hidden rounded-bl-border-radius-sm rounded-br-border-radius-sm">
              <div className="flex flex-row items-center">
                <img
                  src={ICONS?.REIMBURSEMENT_TRANSACTION_BLUE_CIRCLE}
                  alt="REIMBURSEMENT_TRANSACTION_BLUE_CIRCLE"
                  className="h-icon-lg w-icon-lg mr-margin-sm"
                />
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <p className="text-primary-bg font-semibold text-font-md">
                      Reimbursement
                    </p>
                  </div>
                  <p className="text-primary-bg text-font-xs">Transactions</p>
                </div>
              </div>
            </div>
            <div className="mt-margin-button-l Qsmd:mt-margin-base">
              <TopBarButtons
                services={services}
                items={[
                  {
                    imgColor: "var(--text-primary)",
                    icon: ICONS?.ADD_BUTTON_WHITE,
                    label: <>Create reimbursement transaction</>,
                    requiredservice: [
                      "CReimbursementTxn",
                      "AReimbursementRequest",
                    ],
                    onClick: () =>
                      navigate(
                        "/reimbursement_transaction_dashboard/create_reimbursement_transactions",
                      ),
                  },
                ]}
              />
            </div>

            <div className="w-width-full bg-primary-card Qlg:mt-margin-md md:mt-margin-md sm:mt-margin-md mt-margin-md rounded-border-radius-md relative overflow-hidden">
              <h2 className="bg-primary-card flex items-center justify-between pl-padding-md text-text-primary transition-all duration-animation-medium">
                <div className="flex items-center text-font-sm font-semibold">
                  <img
                    src={ICONS?.APPROVAL_STATUS}
                    alt="transaction header"
                    className="w-icon-sm mr-margin-sm"
                  />
                  Approval Statuses
                </div>
                <div className="flex">
                  <div className="border-dividing"></div>
                  <TertiaryTopRoundButton
                    btnText={TEXT?.VIEW_ALL}
                    icon={ICONS?.ARROW_RIGHT_WHITE}
                    imgColor="var(--text-primary)"
                    btnType="button"
                    hoverOption="white"
                    handleFunction={async () => {
                      const hasVReimbursementTxn =
                        Array.isArray(services) &&
                        services.includes("VReimbursementTxn");

                      if (hasVReimbursementTxn) {
                        dispatch(resetToggle());
                        navigate(
                          "/reimbursement_transaction_dashboard/reimbursement_transaction_view_all",
                        );
                      } else {
                        navigate("/feature_not_assigned", {
                          state: { unavailableService: "VReimbursementTxn" },
                        });
                      }
                    }}
                    isDisabled={isViewAllDisabled}
                    enabledTextColor={"var(--text-primary)"}
                    disabledTextColor={"var(--text-secondary)"}
                    enabledBgColor={"var(--primary-card)"}
                  />
                </div>
              </h2>

              <div className="flex flex-col Qmd:flex-row gap-gap-base p-padding-base bg-primary-nav">
                <CommonCardDashboard
                  label={TEXT?.REQUIRE_APPROVAL}
                  count={
                    hasTxnApprovalService
                      ? requireApprovalTxnCount || "00"
                      : "N/A"
                  }
                  color="var(--pending)"
                  loader={txnCountLoading || reimbursementTxnViewAllLoading}
                  onClick={() => {
                    navigate(
                      "/reimbursement_transaction_dashboard/reimbursement_transaction_single_approval",
                    );
                  }}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "AReimbursementTxn" },
                    })
                  }
                />

                <div className="border-border border-primary-bg Qsmd:my-margin-md my-margin-base" />
                <CommonCardDashboard
                  label={TEXT?.PENDING_TRANSACTIONS}
                  count={pendingTxnCount ?? "00"}
                  color="var(--pending)"
                  onClick={() => {
                    if (pendingTxnCount !== "00") {
                      handleDatacardClick("pending", "");
                    }
                  }}
                  loader={txnCountLoading || reimbursementTxnViewAllLoading}
                  isDisabled={
                    pendingTxnCount === GlobalConfiguration?.lengthCheckZero ||
                    txnTotalCount === GlobalConfiguration?.lengthCheckZero
                  }
                />
                <div className="border-border border-primary-bg Qsmd:my-margin-md my-margin-base" />
                <CommonCardDashboard
                  label={TEXT?.APPROVED_TRANSACTIONS}
                  count={approvedTxnCount ?? "00"}
                  color="var(--awaiting)"
                  onClick={() => {
                    if (approvedTxnCount !== "00") {
                      handleDatacardClick("approved", "");
                    }
                  }}
                  loader={txnCountLoading || reimbursementTxnViewAllLoading}
                  isDisabled={
                    approvedTxnCount === GlobalConfiguration?.lengthCheckZero ||
                    txnTotalCount === GlobalConfiguration?.lengthCheckZero
                  }
                />
              </div>
            </div>

            <div className="w-width-full mt-margin-md bg-primary-nav rounded-t-border-radius-md rounded-b-border-radius-md">
              <h2 className="bg-primary-card flex items-center justify-between px-padding-md py-padding-sm text-font-sm font-semibold text-text-secondary transition-all duration-animation-medium rounded-t-border-radius-md">
                <span className="flex items-center text-font-sm font-semibold">
                  <img
                    src={ICONS?.STATISTICS}
                    alt="Statistics Header"
                    className="h-icon-sm mr-margin-sm"
                  />
                  Statistics
                </span>
                {slides.length > GlobalConfiguration?.lengthCheckZero && (
                  <div className="flex items-center">
                    <div
                      onClick={prevSlide}
                      className={`cursor-pointer w-icon-sm h-icon-sm flex items-center justify-center mr-margin-sm ${
                        currentSlide === GlobalConfiguration?.lengthCheckZero
                          ? "opacity-light-opacity cursor-not-allowed"
                          : ""
                      }`}
                      style={{
                        pointerEvents:
                          currentSlide === GlobalConfiguration?.lengthCheckZero
                            ? "none"
                            : "auto",
                      }}
                    >
                      <img
                        src={ICONS?.ARROW_LEFT_WHITE}
                        alt="Previous"
                        className="w-width-full h-height-full"
                      />
                    </div>
                    <div
                      onClick={nextSlide}
                      className={`cursor-pointer w-icon-sm h-icon-sm flex items-center justify-center ${
                        currentSlide ===
                        slides.length - GlobalConfiguration?.lengthCheckOne
                          ? "opacity-light-opacity cursor-not-allowed"
                          : ""
                      }`}
                      style={{
                        pointerEvents:
                          currentSlide ===
                          slides.length - GlobalConfiguration?.lengthCheckOne
                            ? "none"
                            : "auto",
                      }}
                    >
                      <img
                        src={ICONS?.ARROW_RIGHT_WHITE}
                        alt="Next"
                        className="w-width-full h-height-full"
                      />
                    </div>
                  </div>
                )}
              </h2>

              <div className="relative w-width-full lg:py-padding-md overflow-hidden contents lg:block">
                {slides.length > GlobalConfiguration?.lengthCheckZero ? (
                  <div
                    className="flex transition-transform duration-animation-medium ease-in-out"
                    style={{
                      transform: `translateX(-${currentSlide * TimelineConfiguration?.timeDuration}%)`,
                    }}
                  >
                    {slides.map((slide, index) => (
                      <div
                        key={index}
                        className="w-width-full flex-shrink-0 flex items-center justify-center"
                      >
                        {slide}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center lg:justify-start h-height-full text-center p-padding-md">
                    <div className="flex flex-col justify-center items-center w-width-full">
                      <img
                        src={ICONS?.NO_STATISTICS_AVAILABLE}
                        alt="No Slides"
                        className="mb-margin-md"
                      />
                      <p className="text-font-xs text-text-primary">
                        No statistics available due to the absence of data.
                        <br /> Once data is present, statistics will appear.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-y-gap-md Qlg:w-dashboard-right-card-width Qlg:absolute Qlg:h-full Qlg:right-right-base">
            <WeeklySchedulesCard
              header={{
                title: "Reimbursement Requests",
                icon: ICONS.CALENDER,
              }}
              approval={{
                service: "AReimbursementRequest",
                count: pendingRequestCount,
                label: "Require approval",
                onClick: handleRequestNavigation,
                isDisabled: reimbursementRequestViewAllLoading,
              }}
              list={{
                loading: reimbursementRequestViewAllLoading,
                emptyText: "There are no reimbursement requests.",
                data: requests,
                getTitle: (row) => row?.beneficiary_account_name || "-",
                getID: (row) => row?.request_id,
                getDate: (row) =>
                  `INR ${formatTotalBalance(Number(row?.requested_amount) || GlobalConfiguration?.lengthCheckZero)?.amount}`,
                titleLabel: "Account name",
                dateLabel: "Requested amount",
                labelColor: "var(--settled)",
                handleClick: (id) => {
                  navigate(
                    `/reimbursement_transaction_dashboard/reimbursement_request_view_all/reimbursement_request_detail_page/${id}`,
                  );
                },
              }}
              services={services}
              formatPendingCount={formatPendingCount}
              onFeatureNotAssigned={(missingServices) =>
                navigate("/feature_not_assigned", {
                  state: { unavailableService: missingServices },
                })
              }
              actions={[
                {
                  label: "Create request",
                  service: "CReimbursementTxn",
                  isDisabled: hasCReimbursementTxn && hasAReimbursementRequest,
                  onClick: handleCreateRequest,
                  enabledBgColor: "var(--primary-nav-act)",
                  enabledTextColor: "var(--text-primary)",
                  outLineColor: "",
                  hoverOption: "white",
                  fullWidthRequired: true,
                },
                {
                  label: "View requests",
                  service: "VReimbursementTxn",
                  onClick: handleViewRequest,
                  enabledBgColor: "var(--primary-nav-act)",
                  enabledTextColor: "var(--text-primary)",
                  outLineColor: "",
                  hoverOption: "white",
                  fullWidthRequired: true,
                },
              ]}
            />

            {/* <div className="w-full">
              <CommonMoreActionsCard
                title="More actions"
                actions={[
                  {
                    label: "MIS report",
                    service: "SMisUser",
                    onClick: () => {
                      const dashboardBasePath =
                        "/reimbursement_transaction_dashboard";

                      if ((services || []).includes("SMisUser")) {
                        navigate(`${dashboardBasePath}/generate_mis_report`);
                      } else {
                        navigate("/feature_not_assigned", {
                          state: { unavailableService: "SMisUser" },
                        });
                      }
                    },
                  },
                ]}
              />
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReimbursementTransactionsDashboard;
